import React, { useState, useEffect, useRef } from 'react';
import { sendMessageToGemini, generateTimetableSuggestion } from '../../services/gemini';
import type { ChatMessage } from '../../services/gemini';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { buildSchoolContext, formatContextForAI } from '../../services/aiDataFetcher';
import {
  generateTimetableExcelTemplate,
  generateBulkTimetableExcelTemplate,
  downloadExcelBlob,
  formatTeacherAvailabilityForAI,
  fetchTimetableForModification,
  swapTimetablePeriods,
  updateTimetablePeriod,
  fetchTeachersForTimetable,
  formatTimetableForAI,
  formatModificationResultForAI,
  getAllTimetablesForClass,
  formatClassTimetablesForAI,
} from '../../services/aiTimetableService';

interface FloatingAIChatProps {
  domain: string;
  externalOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

interface ExtendedChatMessage extends ChatMessage {
  attachments?: {
    name: string;
    type: string;
    size: number;
    data: string;
  }[];
  downloadableFile?: {
    name: string;
    content: string;
    type: string;
  };
}

const FloatingAIChat: React.FC<FloatingAIChatProps> = ({ domain, externalOpen, onOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Sync with external open state
  useEffect(() => {
    if (externalOpen !== undefined && externalOpen !== isOpen) {
      setIsOpen(externalOpen);
    }
  }, [externalOpen]);

  // Notify parent when open state changes
  const handleSetIsOpen = (newState: boolean) => {
    setIsOpen(newState);
    onOpenChange?.(newState);
  };
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTimetableForm, setShowTimetableForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateGenerating, setTemplateGenerating] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [schoolContext, setSchoolContext] = useState<string>('');
  const [contextLoaded, setContextLoaded] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Timetable generation states
  const [classData, setClassData] = useState({
    className: '',
    section: '',
    subjects: [] as string[],
    teachers: [] as { name: string; subject: string }[],
    periodsPerDay: 8,
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  });

  // Template generation states
  const [templateData, setTemplateData] = useState({
    className: '10',
    section: 'A',
    templateType: 'single' as 'single' | 'bulk',
  });

  const [availableClasses, setAvailableClasses] = useState<string[]>([]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load initial data when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadClasses();
      loadSchoolContext();
      // Add welcome message
      setMessages([
        {
          role: 'model',
          parts: 'Hi! I\'m your AI assistant. I can help you with:\n\n📅 **Timetable Design** - Create timetables using your school\'s actual teachers\n🔧 **Timetable Modification** - Swap periods, change teachers, update schedules\n📋 **Schedule Management** - Exam schedules, events, calendars\n👨‍🏫 **Teacher Assignment** - Match teachers to subjects based on expertise\n✅ **Availability Check** - I check teacher availability before suggesting assignments!\n📊 **Data Insights** - Performance analysis, attendance patterns\n\n💡 **I have access to your school\'s real data:**\n• Teachers (with subjects they teach)\n• Existing timetables & schedules\n• Class information\n• **Real-time teacher availability**\n\n**Try asking:**\n• "Design a timetable for Class 10A Monday"\n• "Show timetable for Class 10A Monday"\n• "Swap period 2 and period 5 for Class 10A Monday"\n• "Change teacher for period 3 to Mr. Kumar"\n• "Who is available to teach Math on Tuesday?"\n\n⚠️ I will warn you if a teacher is already busy at a specific time!\n\nWhat can I help you with?',
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [isOpen]);

  const loadClasses = async () => {
    try {
      const classesRef = collection(db, `school-data/${domain}/classes`);
      const snapshot = await getDocs(classesRef);
      const classes = snapshot.docs.map(doc => doc.id);
      setAvailableClasses(classes);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  // Load school context from Firestore
  const loadSchoolContext = async () => {
    if (!domain || contextLoaded) {
      console.log('⚠️ Skipping context load - domain:', domain, 'contextLoaded:', contextLoaded);
      return;
    }

    console.log('🔍 Loading school context from Firestore for domain:', domain);
    try {
      const context = await buildSchoolContext(domain);
      if (context) {
        const formattedContext = formatContextForAI(context);
        setSchoolContext(formattedContext);
        setContextLoaded(true);
        console.log('✅ School context loaded successfully');
      } else {
        console.log('⚠️ No context returned from buildSchoolContext');
      }
    } catch (error) {
      console.error('❌ Error loading school context:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      setUploadedFiles(prev => [...prev, ...fileArray]);
    }
  };

  // Remove uploaded file
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle download file
  const downloadFile = (filename: string, content: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to detect day of week in message
  const detectDayInMessage = (message: string): string | null => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const lowercaseMessage = message.toLowerCase();

    for (const day of days) {
      if (lowercaseMessage.includes(day)) {
        return day.charAt(0).toUpperCase() + day.slice(1);
      }
    }
    return null;
  };

  // Helper to check if message is about timetable/scheduling
  const isTimetableRelatedMessage = (message: string): boolean => {
    const keywords = ['timetable', 'schedule', 'period', 'class', 'teacher', 'assign', 'design', 'create', 'swap', 'change', 'update', 'modify', 'reschedule', 'show'];
    const lowercaseMessage = message.toLowerCase();
    return keywords.some(keyword => lowercaseMessage.includes(keyword));
  };

  // Helper to detect timetable modification commands
  const detectModificationCommand = (message: string): {
    type: 'swap' | 'change_teacher' | 'update_subject' | 'view' | null;
    classId?: string;
    day?: string;
    period1?: number;
    period2?: number;
    periodNumber?: number;
    teacherNameOrId?: string;
    newSubject?: string;
  } | null => {
    const lowerMessage = message.toLowerCase();

    // Detect class and day
    const classMatch = lowerMessage.match(/class\s*(\d+)\s*([a-z])?/i);
    const classId = classMatch ? `${classMatch[1]}${classMatch[2] || 'a'}` : null;
    const day = detectDayInMessage(message);

    // Detect swap command: "swap period 2 and period 5"
    const swapMatch = lowerMessage.match(/swap\s+period\s*(\d+)\s*(?:and|with|&)\s*period\s*(\d+)/i);
    if (swapMatch) {
      return {
        type: 'swap',
        classId: classId || undefined,
        day: day || undefined,
        period1: parseInt(swapMatch[1]),
        period2: parseInt(swapMatch[2]),
      };
    }

    // Detect change teacher command: "change teacher for period 3 to Mr. Kumar"
    const changeTeacherMatch = lowerMessage.match(/(?:change|assign|set|replace)\s+(?:the\s+)?teacher\s+(?:for\s+)?period\s*(\d+)\s+to\s+(.+?)(?:\s+for|\s+on|\s*$)/i);
    if (changeTeacherMatch) {
      return {
        type: 'change_teacher',
        classId: classId || undefined,
        day: day || undefined,
        periodNumber: parseInt(changeTeacherMatch[1]),
        teacherNameOrId: changeTeacherMatch[2].trim(),
      };
    }

    // Detect view timetable command: "show timetable for class 10A Monday"
    const viewMatch = lowerMessage.match(/(?:show|view|display|get)\s+(?:the\s+)?(?:current\s+)?timetable/i);
    if (viewMatch && classId) {
      return {
        type: 'view',
        classId,
        day: day || undefined,
      };
    }

    return null;
  };

  // Execute timetable modification
  const executeTimetableModification = async (command: NonNullable<ReturnType<typeof detectModificationCommand>>): Promise<string> => {
    if (!command.classId || !command.day) {
      return '❌ Please specify the class and day for the modification. Example: "Swap period 2 and period 5 for Class 10A on Monday"';
    }

    // Format classId: user might say "10A" or "class10a" - normalize to what's in Firestore
    // Firestore stores classId as "class10a" format
    let normalizedClassId = command.classId.toLowerCase();
    if (!normalizedClassId.startsWith('class')) {
      normalizedClassId = `class${normalizedClassId}`;
    }
    console.log(`📍 Normalized classId: ${normalizedClassId}`);

    try {
      switch (command.type) {
        case 'swap': {
          if (!command.period1 || !command.period2) {
            return '❌ Please specify both period numbers to swap.';
          }
          console.log(`🔄 Swapping periods ${command.period1} and ${command.period2} for ${normalizedClassId} on ${command.day}`);
          const result = await swapTimetablePeriods(
            domain,
            normalizedClassId,
            command.day,
            command.period1,
            command.period2
          );
          return formatModificationResultForAI(result);
        }

        case 'change_teacher': {
          if (!command.periodNumber || !command.teacherNameOrId) {
            return '❌ Please specify the period number and new teacher.';
          }
          console.log(`👨‍🏫 Changing teacher for period ${command.periodNumber} to ${command.teacherNameOrId}`);

          // Find teacher by name or ID
          const teachers = await fetchTeachersForTimetable(domain);
          const teacher = teachers.find(t =>
            t.teacherId.toLowerCase() === command.teacherNameOrId!.toLowerCase() ||
            t.name.toLowerCase().includes(command.teacherNameOrId!.toLowerCase())
          );

          if (!teacher) {
            const availableTeachers = teachers.slice(0, 5).map(t => `• ${t.name} (${t.teacherId})`).join('\n');
            return `❌ Teacher "${command.teacherNameOrId}" not found.\n\n**Available teachers:**\n${availableTeachers}\n\nPlease try again with a valid teacher name or ID.`;
          }

          const result = await updateTimetablePeriod(domain, {
            type: 'change_teacher',
            classId: normalizedClassId,
            day: command.day,
            periodNumber: command.periodNumber,
            newTeacherId: teacher.teacherId,
            newTeacherName: teacher.name,
          });
          return formatModificationResultForAI(result);
        }

        case 'view': {
          console.log(`📋 Viewing timetable for ${normalizedClassId} on ${command.day || 'all days'}`);

          if (command.day) {
            const timetable = await fetchTimetableForModification(
              domain,
              normalizedClassId,
              command.day
            );
            if (!timetable) {
              return `❌ No timetable found for Class ${command.classId} on ${command.day}`;
            }
            return `📅 **Current Timetable for Class ${command.classId} - ${command.day}**\n${formatTimetableForAI(timetable)}\n\n💡 You can modify this timetable by saying:\n• "Swap period 2 and period 5"\n• "Change teacher for period 3 to [Teacher Name]"`;
          } else {
            const timetables = await getAllTimetablesForClass(domain, normalizedClassId);
            if (timetables.length === 0) {
              return `❌ No timetables found for Class ${command.classId}`;
            }
            return formatClassTimetablesForAI(timetables);
          }
        }

        default:
          return '❌ Unknown modification command.';
      }
    } catch (error: any) {
      console.error('Error executing modification:', error);
      return `❌ Error executing modification: ${error.message}`;
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && uploadedFiles.length === 0) return;

    // Prepare attachments
    const attachments = await Promise.all(
      uploadedFiles.map(async (file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        data: await fileToBase64(file),
      }))
    );

    const userMessage: ExtendedChatMessage = {
      role: 'user',
      parts: inputMessage || 'Uploaded files',
      timestamp: new Date().toISOString(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    setUploadedFiles([]);
    setIsLoading(true);

    try {
      // Check for timetable modification commands first
      const modificationCommand = detectModificationCommand(currentInput);

      if (modificationCommand && modificationCommand.type) {
        console.log('🔧 Detected timetable modification command:', modificationCommand);

        // Execute the modification directly
        const modificationResult = await executeTimetableModification(modificationCommand);

        const aiMessage: ExtendedChatMessage = {
          role: 'model',
          parts: modificationResult,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, aiMessage]);
        setIsLoading(false);
        return;
      }

      // Create context with file information
      let messageWithContext = currentInput;
      if (attachments.length > 0) {
        const fileInfo = attachments.map(f => `File: ${f.name} (${f.type}, ${(f.size / 1024).toFixed(2)}KB)`).join('\n');
        messageWithContext = `${currentInput}\n\nAttached files:\n${fileInfo}`;
      }

      // Check if message is about timetable and mentions a specific day
      let enhancedContext = schoolContext;
      const detectedDay = detectDayInMessage(currentInput);

      if (detectedDay && isTimetableRelatedMessage(currentInput)) {
        console.log(`📅 Detected day "${detectedDay}" in timetable request - fetching teacher availability`);
        try {
          const availabilityContext = await formatTeacherAvailabilityForAI(domain, detectedDay);
          enhancedContext = schoolContext + availabilityContext;
          console.log('✅ Teacher availability added to context');
        } catch (error) {
          console.error('⚠️ Could not fetch teacher availability:', error);
        }
      }

      // Send message with school context (including availability if detected) for smarter responses
      const response = await sendMessageToGemini(messageWithContext, messages, enhancedContext);

      // Check if response contains downloadable content (e.g., generated timetable)
      let downloadableFile;
      if (response.includes('```') || response.toLowerCase().includes('timetable') || response.toLowerCase().includes('schedule')) {
        // Extract potential downloadable content
        const codeMatch = response.match(/```(\w*)\n([\s\S]*?)```/);
        if (codeMatch) {
          downloadableFile = {
            name: `ai-generated-${Date.now()}.txt`,
            content: codeMatch[2],
            type: 'text/plain',
          };
        }
      }

      const aiMessage: ExtendedChatMessage = {
        role: 'model',
        parts: response,
        timestamp: new Date().toISOString(),
        downloadableFile,
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: ExtendedChatMessage = {
        role: 'model',
        parts: 'Sorry, I encountered an error. Please check your API configuration in the .env file.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateTimetable = async () => {
    if (!classData.className || !classData.section) {
      alert('Please select class and section');
      return;
    }

    setIsLoading(true);
    setShowTimetableForm(false);

    try {
      const response = await generateTimetableSuggestion(classData);
      const aiMessage: ChatMessage = {
        role: 'model',
        parts: response,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        role: 'model',
        parts: 'Failed to generate timetable. Please check your configuration.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Excel template generation
  const handleGenerateExcelTemplate = async () => {
    setTemplateGenerating(true);
    setShowTemplateForm(false);

    // Add a message showing template is being generated
    const generatingMessage: ExtendedChatMessage = {
      role: 'model',
      parts: `📊 Generating Excel timetable template for ${templateData.templateType === 'bulk' ? 'multiple classes' : `Class ${templateData.className}${templateData.section}`}...\n\nThis will include:\n• Your school's actual teachers\n• Available subjects\n• Standard period timings\n• Instructions sheet`,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, generatingMessage]);

    try {
      let blob: Blob;
      let filename: string;

      if (templateData.templateType === 'bulk') {
        // Generate bulk template for multiple classes
        const classes = [
          { className: '1', section: 'A' },
          { className: '1', section: 'B' },
          { className: '2', section: 'A' },
          { className: '2', section: 'B' },
          { className: '3', section: 'A' },
          { className: '3', section: 'B' },
        ];
        blob = await generateBulkTimetableExcelTemplate(domain, classes);
        filename = `timetable_bulk_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      } else {
        // Generate single class template
        blob = await generateTimetableExcelTemplate(domain, {
          className: templateData.className,
          section: templateData.section,
          includeTeachers: true,
          periods: 8,
        });
        filename = `timetable_class${templateData.className}${templateData.section}_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      }

      // Download the file
      downloadExcelBlob(blob, filename);

      // Add success message
      const successMessage: ExtendedChatMessage = {
        role: 'model',
        parts: `✅ **Excel Template Generated Successfully!**\n\n📥 File downloaded: **${filename}**\n\n**What's included:**\n• Timetable sheet with your school's teachers pre-filled\n• Teachers Reference sheet (all teachers with IDs)\n• Subjects Reference sheet (subjects mapped to teachers)\n• Instructions sheet\n\n**Next steps:**\n1. Open the downloaded Excel file\n2. Modify the subjects and teachers as needed\n3. Go to **SIS > Time Table > Upload**\n4. Upload the modified file\n\n💡 *Tip: Use Teacher IDs from the "Teachers Reference" sheet for accurate assignment.*`,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, successMessage]);
    } catch (error) {
      console.error('Error generating template:', error);
      const errorMessage: ExtendedChatMessage = {
        role: 'model',
        parts: '❌ Failed to generate Excel template. Please try again or check if you have teachers in your database.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setTemplateGenerating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickPrompts = [
    'Design timetable for Class 10A Monday',
    'Show all teachers',
    'What subjects are available?',
    'Help with scheduling',
  ];

  if (!isOpen) {
    return (
      <button
        className="btn btn-primary-custom rounded-circle shadow-lg"
        onClick={() => handleSetIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          width: '60px',
          height: '60px',
          zIndex: 1000,
          border: 'none',
        }}
      >
        <i className="bi bi-robot" style={{ fontSize: '1.8rem' }}></i>
      </button>
    );
  }

  return (
    <div
      className="card shadow-lg"
      style={{
        position: 'fixed',
        bottom: isMinimized ? '-500px' : '20px',
        right: '20px',
        width: '400px',
        height: isMinimized ? '60px' : '600px',
        zIndex: 1000,
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div className="card-header bg-primary-custom text-white p-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-robot" style={{ fontSize: '1.5rem' }}></i>
          <div>
            <h6 className="mb-0 d-flex align-items-center gap-2">
              AI Assistant
              {contextLoaded && (
                <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>
                  <i className="bi bi-database-check me-1"></i>Data Connected
                </span>
              )}
            </h6>
            <small className="opacity-75" style={{ fontSize: '0.7rem' }}>Powered by Gemini</small>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm text-white p-0"
            onClick={() => setIsMinimized(!isMinimized)}
            style={{ background: 'none', border: 'none' }}
          >
            <i className={`bi ${isMinimized ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </button>
          <button
            className="btn btn-sm text-white p-0"
            onClick={() => handleSetIsOpen(false)}
            style={{ background: 'none', border: 'none' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      {/* Chat Body */}
      {!isMinimized && (
        <>
          <div className="card-body p-3 overflow-auto flex-grow-1" style={{ maxHeight: '400px' }}>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
              >
                <div
                  className={`p-2 rounded-3 ${
                    msg.role === 'user'
                      ? 'bg-primary-custom text-white'
                      : 'bg-light border'
                  }`}
                  style={{ maxWidth: '85%', fontSize: '0.9rem' }}
                >
                  {msg.role === 'model' && (
                    <div className="d-flex align-items-center gap-1 mb-1">
                      <i className="bi bi-robot text-primary-custom" style={{ fontSize: '0.9rem' }}></i>
                      <strong className="text-primary-custom" style={{ fontSize: '0.8rem' }}>AI</strong>
                    </div>
                  )}
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>{msg.parts}</div>

                  {/* Display attachments */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-2">
                      {msg.attachments.map((file, fileIndex) => (
                        <div key={fileIndex} className="badge bg-secondary me-1 mb-1" style={{ fontSize: '0.7rem' }}>
                          <i className="bi bi-paperclip me-1"></i>
                          {file.name} ({(file.size / 1024).toFixed(1)}KB)
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Download button for AI generated files */}
                  {msg.downloadableFile && (
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => downloadFile(
                          msg.downloadableFile!.name,
                          msg.downloadableFile!.content,
                          msg.downloadableFile!.type
                        )}
                        style={{ fontSize: '0.75rem' }}
                      >
                        <i className="bi bi-download me-1"></i>
                        Download {msg.downloadableFile.name}
                      </button>
                    </div>
                  )}

                  <small className={`d-block mt-1 opacity-75 ${msg.role === 'user' ? 'text-end' : ''}`} style={{ fontSize: '0.7rem' }}>
                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                  </small>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="d-flex justify-content-start mb-3">
                <div className="bg-light border p-2 rounded-3">
                  <div className="spinner-border spinner-border-sm text-primary-custom me-2" role="status"></div>
                  <span style={{ fontSize: '0.85rem' }}>AI is thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Timetable Form */}
          {showTimetableForm && (
            <div className="px-3 py-2 border-top bg-light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong style={{ fontSize: '0.9rem' }}>Timetable Generator</strong>
                <button
                  className="btn btn-sm btn-close"
                  onClick={() => setShowTimetableForm(false)}
                ></button>
              </div>
              <div className="mb-2">
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Class</label>
                <select
                  className="form-select form-select-sm"
                  value={classData.className}
                  onChange={(e) => setClassData({ ...classData, className: e.target.value })}
                >
                  <option value="">Select Class</option>
                  {availableClasses.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Section</label>
                <select
                  className="form-select form-select-sm"
                  value={classData.section}
                  onChange={(e) => setClassData({ ...classData, section: e.target.value })}
                >
                  <option value="">Select Section</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
              <button
                className="btn btn-primary-custom btn-sm w-100"
                onClick={handleGenerateTimetable}
                disabled={isLoading || !classData.className || !classData.section}
              >
                {isLoading ? 'Generating...' : 'Generate Timetable'}
              </button>
            </div>
          )}

          {/* Excel Template Form */}
          {showTemplateForm && (
            <div className="px-3 py-2 border-top bg-light" style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong style={{ fontSize: '0.9rem' }}>
                  <i className="bi bi-file-earmark-excel me-1 text-success"></i>
                  Excel Template Generator
                </strong>
                <button
                  className="btn btn-sm btn-close"
                  onClick={() => setShowTemplateForm(false)}
                ></button>
              </div>
              <p className="small text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                Generate a timetable Excel template pre-filled with your school's teachers and subjects.
              </p>

              {/* Template Type */}
              <div className="mb-2">
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Template Type</label>
                <div className="d-flex gap-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="templateType"
                      id="singleClass"
                      checked={templateData.templateType === 'single'}
                      onChange={() => setTemplateData({ ...templateData, templateType: 'single' })}
                    />
                    <label className="form-check-label" htmlFor="singleClass" style={{ fontSize: '0.8rem' }}>
                      Single Class
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="templateType"
                      id="bulkClasses"
                      checked={templateData.templateType === 'bulk'}
                      onChange={() => setTemplateData({ ...templateData, templateType: 'bulk' })}
                    />
                    <label className="form-check-label" htmlFor="bulkClasses" style={{ fontSize: '0.8rem' }}>
                      Bulk (Multiple Classes)
                    </label>
                  </div>
                </div>
              </div>

              {/* Single Class Options */}
              {templateData.templateType === 'single' && (
                <>
                  <div className="mb-2">
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Class</label>
                    <select
                      className="form-select form-select-sm"
                      value={templateData.className}
                      onChange={(e) => setTemplateData({ ...templateData, className: e.target.value })}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={(i + 1).toString()}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Section</label>
                    <select
                      className="form-select form-select-sm"
                      value={templateData.section}
                      onChange={(e) => setTemplateData({ ...templateData, section: e.target.value })}
                    >
                      {['A', 'B', 'C', 'D', 'E', 'F'].map(sec => (
                        <option key={sec} value={sec}>{sec}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {templateData.templateType === 'bulk' && (
                <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Will generate template for Classes 1A, 1B, 2A, 2B, 3A, 3B
                </div>
              )}

              <button
                className="btn btn-success btn-sm w-100"
                onClick={handleGenerateExcelTemplate}
                disabled={templateGenerating}
              >
                {templateGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-download me-1"></i>
                    Generate & Download Template
                  </>
                )}
              </button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="px-3 py-2 bg-light border-top">
            <div className="d-flex gap-1 flex-wrap mb-2">
              {quickPrompts.map((prompt, index) => (
                <button
                  key={index}
                  className="btn btn-sm btn-outline-primary rounded-pill"
                  onClick={() => setInputMessage(prompt)}
                  disabled={isLoading || templateGenerating}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  {prompt}
                </button>
              ))}
              <button
                className="btn btn-sm btn-outline-success rounded-pill"
                onClick={() => {
                  setShowTimetableForm(!showTimetableForm);
                  setShowTemplateForm(false);
                }}
                disabled={isLoading || templateGenerating}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                <i className="bi bi-calendar-week me-1"></i>Timetable
              </button>
              <button
                className="btn btn-sm btn-outline-warning rounded-pill"
                onClick={() => {
                  setShowTemplateForm(!showTemplateForm);
                  setShowTimetableForm(false);
                }}
                disabled={isLoading || templateGenerating}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                title="Generate Excel Template"
              >
                <i className="bi bi-file-earmark-excel me-1"></i>Excel Template
              </button>
            </div>
          </div>

          {/* Input Area */}
          <div className="card-footer bg-white border-top p-2">
            {/* Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
              <div className="mb-2 d-flex gap-1 flex-wrap">
                {uploadedFiles.map((file, index) => (
                  <span key={index} className="badge bg-info d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                    <i className="bi bi-file-earmark"></i>
                    {file.name}
                    <button
                      className="btn-close btn-close-white"
                      style={{ fontSize: '0.5rem' }}
                      onClick={() => removeFile(index)}
                    ></button>
                  </span>
                ))}
              </div>
            )}

            <div className="input-group input-group-sm">
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                accept=".txt,.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
              />

              {/* File upload button */}
              <button
                className="btn btn-outline-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                title="Attach files"
              >
                <i className="bi bi-paperclip"></i>
              </button>

              <textarea
                className="form-control"
                placeholder="Ask me anything..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={2}
                disabled={isLoading}
                style={{ fontSize: '0.85rem', resize: 'none' }}
              />
              <button
                className="btn btn-primary-custom"
                onClick={handleSendMessage}
                disabled={isLoading || (!inputMessage.trim() && uploadedFiles.length === 0)}
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </div>
            <small className="text-muted mt-1 d-block" style={{ fontSize: '0.65rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Supports: txt, pdf, doc, xlsx, images
            </small>
          </div>
        </>
      )}
    </div>
  );
};

export default FloatingAIChat;
